<template>
  <form
    class="space-y-6"
    novalidate
    @submit="onFormElementSubmit"
  >
    <FormInputText
      name="tenantIdentifier"
      label="Merchant Identifier"
      autocomplete="off"
    />

    <FormInputText
      name="email"
      type="email"
      autocomplete="email"
      label="Email Address"
    />

    <FormInputPassword
      name="password"
      label="Password"
      autocomplete="current-password"
    />

    <div class="flex items-center justify-between">
      <RouterLink
        :to="{
          name: 'resetPasswordRequest',
          params: { userType: 'merchant-user' },
        }"
        class="text-sm text-primary hover:text-primary/70"
      >
        Forgot password?
      </RouterLink>
      <FormSubmitButton
        :is-submitting="isSubmitting"
        type="submit"
      >
        Sign in with password
      </FormSubmitButton>
    </div>
  </form>

  <UserInactiveAlert ref="userInactiveAlertRef" />
</template>

<script setup lang="ts">
import { logInBasic } from "@/services/auth/logInBasic"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import z from "zod"
import { ApiError } from "@/api"
import {
  emailAddressSchema,
  requiredNonBlankString,
  requiredTrimmedString,
} from "@/validation/sharedStringFieldSchemas"

const router = useRouter()

const formSchema = z.object({
  tenantIdentifier: requiredTrimmedString("MID required"),
  email: emailAddressSchema("Email is required"),
  password: requiredNonBlankString("Password required"),
})

const { handleSubmit, isSubmitting } = useForm({
  validationSchema: toTypedSchema(formSchema),
  initialValues: {
    tenantIdentifier: "",
    email: "",
    password: "",
  },
})

const userInactiveAlertRef = useTemplateRef(
  "userInactiveAlertRef"
)

const onFormElementSubmit = handleSubmit(async (values) => {
  try {
    await logInBasic(values)

    router.push({
      name: "dashboard",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code === "user-inactive") {
        if (userInactiveAlertRef.value !== null) {
          userInactiveAlertRef.value.reveal()
          return
        } else {
          toast.error("User Inactive")
          return
        }
      } else if (error.code === "portal-not-allowed") {
        toast.error("Portal not enabled for this merchant")
      }

      toast.error(
        "Failed to log in. Please check your username and password are correct."
      )
    } else {
      toast.error(
        "Failed to log in. Please check your username and password are correct."
      )
    }
  }
})
</script>
